import { TablePageSelector, TableWrapper, useModal } from '@bitstopco/bitstop-theme';
import { Box, Grid } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';
import { cloneDeep, omit } from 'lodash';
import { useCallback, useMemo, useRef, useState } from 'react';

import TransactionsCardStats from '@/components/transactions/TransactionsCardStats';
import CardTable from '@/components/ui/card/CardTable';

import { useBulkTaggableActions } from '@/hooks/useBulkTaggableActions';
import { useGetAllTagsByTermQuery } from '@/store/api/services/common/tags';
import {
  useGetTransactionsExportMutation,
  useGetTransactionsQuery,
  useGetTransactionsStatsQuery,
} from '@/store/api/services/transactions/transactions';

import { DEFAULT_PAGINATION } from '@/constants/api';

import type { Tag, TransactionsSale, WithTags } from '@/types';
import type {
  ApiListingRequest,
  TablePageSelectorProps,
  TableToolbarFilterConfig,
  TableWrapperRef,
} from '@bitstopco/bitstop-theme';

import { getFilteredSalesConfig } from '@/helpers/roleBasedFiltering';
import { useDispenseAssets } from '@/hooks/useDispenseAssets';
import { useAppSelector } from '@/store';
import { authReducer } from '@bitstopco/bitstop-theme';

/**
 * Transactions Sales Table
 * * For tables we have the TableWrapper component from the Bitstop Theme, for more information please visit the Bitstop Theme documentation
 * @return {*}
 */
const TransactionsSales = () => {
  const { show } = useModal();
  const { assets } = useDispenseAssets();

  const [exportMutation] = useGetTransactionsExportMutation();

  const [payload, setPayload] = useState<ApiListingRequest>();

  const tableRef = useRef<TableWrapperRef>(null);

  // Get user role for column filtering
  const {
    selectors: { userInfo },
  } = authReducer;
  const userData = useAppSelector(userInfo);
  const userRoleName = userData?.role?.name;

  const statsPayload = useMemo(
    () => payload && omit(payload as ApiListingRequest, ['pagination']),
    [payload],
  );

  /**
   * Get the Transactions filtered by the desired payload
   */
  const {
    data: {
      data: apiData = [],
      included,
      meta: { pagination: metaPagination = DEFAULT_PAGINATION } = {},
    } = {},
    isFetching,
  } = useGetTransactionsQuery(payload || skipToken);

  const { data: tagData } = useGetAllTagsByTermQuery({ category: 'transaction', term: '' });
  const tagsList = useMemo(() => tagData?.data ?? [], [tagData]);

  const data: WithTags<TransactionsSale>[] = useMemo(() => {
    const allTags = (included?.tags ?? []) as Tag[];
    return apiData.map((customer) => ({
      ...customer,
      tags: allTags.filter(({ id }) => customer.tag_ids.includes(id)),
    }));
  }, [apiData]);

  const { multiSelectValues, setMultiSelectValues, handleMultiSelectActions, config } =
    useBulkTaggableActions(data, 'transaction');

  /**
   * Get the Transactions stats filtered by the desired payload
   */
  const {
    data: { data: stats } = {},
    isFetching: isFetchingStats,
  } = useGetTransactionsStatsQuery(statsPayload || skipToken);

  const handleRow = ({ id }: TransactionsSale) => {
    // Same drawer as the dashboard; it links out to the single sale view via Open Full View.
    show('transaction-details', { hideUrlParams: true, data: { id } });
  };

  const handleExport = (type: string) => {
    tableRef.current?.getExportFile({
      type,
      mutation: exportMutation,
      fileName: 'Nonce-Sales-Report',
    });
  };

  const renderPageSelector = useCallback(
    (props: TablePageSelectorProps) => (
      <Grid container>
        {!isFetchingStats && stats && (
          <Box mx={3} mb={2} width="100%">
            <TransactionsCardStats stats={stats} />
          </Box>
        )}
        <TablePageSelector {...props} />
      </Grid>
    ),
    [stats, statsPayload, isFetchingStats],
  );

  const { filtersConfig, filteredColumns } = useMemo(() => {
    const { columns, filters } = getFilteredSalesConfig(userRoleName);
    let filteredFilters = cloneDeep(filters) as Record<string, any>[];
    filteredFilters = filteredFilters.map((config) => {
      if (config.property === 'tag_ids') {
        return {
          ...config,
          options: [...tagsList.map(({ id, name, color }) => ({ label: name, value: id, color }))],
        };
      }
      if (config.property === 'coin_id') {
        return {
          ...config,
          options: [
            ...(included?.coins ?? []).map(({ id, ticker, is_token, chain }) => ({
              label: is_token ? `${ticker} / ${chain?.name}` : ticker,
              value: id,
            })),
          ],
        };
      }
      if (config.property === 'asset_id') {
        return {
          ...config,
          options: [...(assets ?? []).map(({ id, name }) => ({ label: name, value: id }))],
        };
      }
      return config;
    });
    return {
      filteredColumns: columns,
      filtersConfig: filteredFilters as TableToolbarFilterConfig[],
    };
  }, [tagsList, userRoleName, included, assets]);

  return (
    <CardTable>
      <TableWrapper<WithTags<TransactionsSale>>
        data={data}
        ref={tableRef}
        dateRangeFilterKey="created_at"
        sortByDefaultValue="created_at"
        isLoading={isFetching || !payload}
        columns={filteredColumns}
        filterConfigs={filtersConfig}
        tableToolbarProps={{
          searchPlaceholder: 'Search by Sale ID, TX Hash, or Wallet Address',
        }}
        renderPageSelector={renderPageSelector}
        totalPages={metaPagination?.total_pages}
        totalItems={metaPagination?.total_items}
        tableProps={{
          placeholder: {
            empty: {
              title: 'No sales found',
              content:
                "We can't find any sale that matches your search, please try a different request",
            },
          },
        }}
        onChange={setPayload}
        onRowClick={handleRow}
        onActionMenuItemClick={handleExport}
        multiSelectValues={multiSelectValues}
        onMultiSelectValuesChange={setMultiSelectValues}
        tableMultiSelectActionsProps={{
          onClick: handleMultiSelectActions,
          name: 'Location(s)',
          config,
          sx: {
            px: 3,
            pb: 2,
            justifyContent: 'flex-end',
          },
        }}
        // fix for bitstop theme Table
        sx={{
          '> .MuiGrid-root': {
            display: 'block',
          },
        }}
      />
    </CardTable>
  );
};

export default TransactionsSales;
